import React, {useId, useState} from "react";
import {DECIMAL_FORMAT, numberFormat} from "../../../util/numbers.js";
import {DONUT_STANDARD_COLORS, getSliceColors} from "./_colors.js";
import orderBy from "lodash/orderBy.js";
import './DonutChart.scss'
import LegendItem from "./LegendItem.jsx";

function polarToCartesian(cx, cy, r, angle) {
    return {
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle),
    }
}

const setZero = val =>
    !val || val.isNaN ? 0 : val

export default ({
                    values = [], // use when wanting specific order (to keep colors consistent)
                    labels = [], // use when wanting specific order (to keep colors consistent)
                    pieLabels = [], // use when wanting specific order (to keep colors consistent)
                    idList = [], // use when wanting specific order (to keep colors consistent)
                    data = [], // use sorting from large to small (and group others)
                    size = 130,
                    thickness = 30,
                    minLabelPercent = 0.05,
                    showPercent = false,
                    colors = DONUT_STANDARD_COLORS,
                    maxSlices = 8, // requires colors.length >= maxSlices
                    children,
                    objectOnClick = null,
                }) => {

    maxSlices = Math.min(maxSlices, colors.length) - 2
    const [hoveredIndex, setHoveredIndex] = useState(null)
    const uuid = useId()

    const cx = size / 2
    const cy = size / 2
    const outerR = size / 2 - 10
    const innerR = outerR - thickness

    let startAngle = -Math.PI / 2

    if (data && data.length >= 1) {
        const dataCleaned = data.map(item => ({
            ...item,
            value: setZero(item.value),
        }))
        const dataOrdered = aggregateAfterIndex(orderBy(dataCleaned, ['value'], ['desc']), maxSlices)
        values = dataOrdered.map(i => i.value)
        labels = dataOrdered.map(i => i.label)
        pieLabels = dataOrdered.map(i => i.pieLabel)
        idList = dataOrdered.map(i => i.id)
    }

    values = values.map(setZero)

    if (values.length === 0) {
        return
    }

    const totalValue = values.reduce((s, v) => s + v, 0)
    const isChartHovered = hoveredIndex !== null

    const fontSizeValue = Math.round(size / 8.25)
    const fontSizeLabel = Math.round(size / 14)

    return (
        <div className='donut-chart flex-vertical-center'>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} id={uuid}>
                <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={'#fff'}
                    style={{pointerEvents: "none", userSelect: "none"}}
                >
                    {hoveredIndex == null ? (
                        <>
                            <tspan x={cx} dy="-0.2em" fontSize={fontSizeValue} fontWeight="700">
                                {numberFormat(totalValue, 0)}
                            </tspan>
                            <tspan x={cx} dy="1.6em" fontSize={fontSizeLabel} fill="#ababab" className='donut-label'>
                                total
                            </tspan>
                        </>
                    ) : (
                        <>
                            <tspan x={cx} dy="-0.2em" fontSize={fontSizeValue} fontWeight="700">
                                {numberFormat(values[hoveredIndex], 0)}
                            </tspan>
                            <tspan x={cx} dy="1.6em" fontSize={fontSizeLabel} fill="#ababab">
                                {pieLabels[hoveredIndex] || labels[hoveredIndex]}
                            </tspan>
                        </>
                    )}
                </text>
                {values.map((value, i) => {

                    if (!value) return null

                    const onClick = objectOnClick && idList[i] ? () => objectOnClick(idList[i]) : () => {
                    }

                    const gradientId = `grad-${uuid}-${i}`

                    const isSliceHovered = i === hoveredIndex

                    const sliceAngle = value === totalValue ? Math.PI * 2 - .000001 : (value / totalValue) * Math.PI * 2

                    const endAngle = startAngle + sliceAngle
                    const midAngle = (startAngle + endAngle) / 2

                    const p1 = polarToCartesian(cx, cy, outerR, startAngle)
                    const p2 = polarToCartesian(cx, cy, outerR, endAngle)
                    const p3 = polarToCartesian(cx, cy, innerR, endAngle)
                    const p4 = polarToCartesian(cx, cy, innerR, startAngle)

                    const frac = totalValue === 0 ? 0 : value / totalValue
                    const largeArcFlag = sliceAngle > Math.PI ? 1 : 0

                    const dDonut = [
                        `M ${p1.x} ${p1.y}`,
                        `A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${p2.x} ${p2.y}`,
                        `L ${p3.x} ${p3.y}`,
                        `A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${p4.x} ${p4.y}`,
                        "Z",
                    ].join(" ")
                    const dPie = [
                        `M ${cx} ${cy}`,
                        `L ${p1.x} ${p1.y}`,
                        `A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${p2.x} ${p2.y}`,
                        "Z",
                    ].join(" ");

                    startAngle = endAngle

                    const {
                        baseColor,
                        lightColor,
                        fontColor,
                    } = getSliceColors(colors, i, isSliceHovered, isChartHovered)

                    const ringMidR = (outerR + innerR) / 2
                    const fx = cx + Math.cos(midAngle) * (ringMidR * 0.25)
                    const fy = cy + Math.sin(midAngle) * (ringMidR * 0.25)

                    return value > 0 && (
                        <g>
                            <path
                                key={gradientId}
                                d={dDonut}
                                fill={`url(#${gradientId})`}
                                stroke="#282c34"
                                strokeWidth="3"
                            >
                                <title>
                                    {value} ({((value / totalValue) * 100).toFixed(1)}%)
                                </title>
                            </path>
                            <path
                                key={i}
                                d={dPie}
                                fill="transparent"
                                strokeWidth="3"
                                onMouseEnter={() => setHoveredIndex(i)}
                                onMouseLeave={() => setHoveredIndex(null)}
                                onClick={onClick}
                            />
                            <radialGradient
                                key={i}
                                id={gradientId}
                                gradientUnits="userSpaceOnUse"
                                cx={cx}
                                cy={cy}
                                r={outerR}
                                fx={fx}
                                fy={fy}
                            >
                                <stop offset="0%" stopColor={baseColor}/>
                                <stop offset="75%" stopColor={baseColor}/>
                                <stop offset="100%" stopColor={lightColor}/>
                            </radialGradient>
                            {frac >= minLabelPercent && (
                                (() => {
                                    const labelR = (outerR + innerR) / 2;
                                    const p = polarToCartesian(cx, cy, labelR, midAngle);

                                    return (
                                        <text
                                            x={p.x}
                                            y={p.y}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fontSize={thickness / 3}
                                            fontWeight={'bold'}
                                            fill={fontColor}
                                            style={{pointerEvents: "none", userSelect: "none"}}
                                        >
                                            {showPercent && `${numberFormat(frac * 100, DECIMAL_FORMAT.FLEX_PERCENTAGE)}%`}
                                        </text>
                                    )
                                })()
                            )}
                        </g>
                    )
                })}
            </svg>
            <div className='legend'>
                {children &&
                <div className='title'>
                    {children}
                </div>
                }
                {values.map((value, i) => {
                    const isSliceHovered = i === hoveredIndex
                    const onClick = objectOnClick && idList[i] ? () => objectOnClick(idList[i]) : () => {
                    }
                    const {
                        baseColor,
                        lightColor,
                        fontColor,
                    } = getSliceColors(colors, i, isSliceHovered, isChartHovered)
                    return (
                        <LegendItem
                            i={i}
                            value={value}
                            label={labels[i]}
                            baseColor={baseColor}
                            lightColor={lightColor}
                            fontColor={fontColor}
                            setHoveredIndex={setHoveredIndex}
                            percent={value / totalValue}
                            isHovered={isSliceHovered}
                            isChartHovered={isChartHovered}
                            onClick={onClick}
                        />
                    )
                })}
            </div>
        </div>

    )
}

function aggregateAfterIndex(arr, n, aggregatedLabel = "other") {
    if (n < 0 || n >= arr.length - 2) return arr;

    const head = arr.slice(0, n + 1);
    const tail = arr.slice(n + 1);

    const aggregatedValue = tail.reduce(
        (sum, item) => sum + item.value,
        0
    );

    return [
        ...head,
        {value: aggregatedValue, label: aggregatedLabel},
    ];
}